import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Input, Button, Modal } from "antd";
import SendMessageModal from "../Modal/SendMessageModal";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../app/stores/store";
import { useOfferLetter } from "../../../app/hooks/useOfferLetter";
import { useCurrentUser } from "../../../app/hooks/useCurrentUser";
import { useOfferLetterById } from "../../../app/hooks/useOfferLetter";

const { TextArea } = Input;

const LetterForm = ({ applicant, onSuccess, status, onClose }) => {
  const { offerLetterStore, notificationStore } = useStore();
  const { showNotification } = notificationStore;
  const { handleFinalSubmit, loading } = offerLetterStore;
  const offerLetterAPI = useOfferLetter();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSend, setIsSend] = useState(true);
  const [form] = Form.useForm();
  const {
    currentUser: { user },
  } = useCurrentUser();

  const { data: offerLetterData } = useOfferLetterById(applicant.offerLetters?.[0]?.applicantId);

  useEffect(() => {
    const latest = Array.isArray(offerLetterData) ? offerLetterData[0] : null;

    form.setFieldsValue({
      recipientName: applicant?.fullName || "",
      subject: latest?.subject || "",
      content: latest?.content || "",
    });
  }, [applicant, offerLetterData, form]);

  const validateForm = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (errorInfo) {
      showNotification("Fill in required fields.", "error", {
        title: "Error",
        duration: 5000,
      });
      return false;
    }
  };

  const handleSubmit = async () => {
    try {
      const values = form.getFieldsValue(true);
      const success = await handleFinalSubmit(values, offerLetterAPI, applicant.id, user.id, status);

      if (success) {
        if (onSuccess) await onSuccess();
        setIsModalVisible(false);
        onClose();
      }
    } catch (error) {
      showNotification("Error submitting form", "error", {
        title: "Submission Failed",
        duration: 5000,
      });
    }
  };

  const handleApproveClick = async () => {
    const isValid = await validateForm();
    if (isValid) {
      setIsSend(true);
      setIsModalVisible(true);
    }
  };

  const handleDeclineClick = () => {
    setIsSend(false);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleConfirmCancel = () => {
    setIsModalVisible(false);
    onClose();
  };

  const getModalSendAction = () => {
    return isSend ? handleSubmit : handleConfirmCancel;
  };

  return (
    <div className="p-8 bg-white rounded-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 m-0 tracking-tight">{offerLetterData ? "View Letter" : "Write a Letter"}</h2>
        <div className="w-80 h-[3px] bg-[#FF7201] mt-1" />
      </div>

      <Form form={form} layout="vertical" className="custom-antd-form">
        <Form.Item name="recipientName" className="mb-2" rules={[{ required: true, message: "Please select an applicant" }]}>
          <Input placeholder="Recipient" size="large" variant="borderless" className="custom-bottom-input" readOnly />
        </Form.Item>

        <Form.Item name="subject" className="mb-2" rules={[{ required: true, message: "Please enter the subject" }]}>
          <Input placeholder="Subject" size="large" variant="borderless" className="custom-bottom-input" readOnly={offerLetterData} />
        </Form.Item>

        <Form.Item name="content" className="mt-4" rules={[{ required: true, message: "Please enter a message" }]}>
          <TextArea
            placeholder="Message..."
            variant="borderless"
            autoSize={{ minRows: 15, maxRows: 20 }}
            className="text-base resize-none custom-bottom-input"
            readOnly={offerLetterData}
          />
        </Form.Item>

        {!offerLetterData && (
          <div className="flex justify-end gap-4 mt-8">
            <Button size="large" className="w-40 !border-[#F45F0C] !text-[#F45F0C] hover:!bg-orange-50 font-semibold rounded-lg" onClick={handleDeclineClick}>
              CANCEL
            </Button>

            <Button
              type="primary"
              size="large"
              className="w-40 border-none font-semibold rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "#F45F0C" }}
              onClick={handleApproveClick}
            >
              SEND
            </Button>
          </div>
        )}
      </Form>

      <Modal open={isModalVisible} centered onCancel={handleModalCancel} footer={null} width={400} destroyOnClose>
        <SendMessageModal onCancel={handleModalCancel} onSaveSuccess={getModalSendAction()} isSend={isSend} isLoading={loading} />
      </Modal>
    </div>
  );
};

LetterForm.propTypes = {
  applicant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    fullName: PropTypes.string.isRequired,
    position: PropTypes.string,
    email: PropTypes.string,
    createdAt: PropTypes.string,
    offerLetters: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        applicantId: PropTypes.string.isRequired,
        senderId: PropTypes.string.isRequired,
        subject: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  status: PropTypes.string,
};

export default observer(LetterForm);
